import {
  Journal,
  JournalAccountCode,
  JournalLine,
  JournalRef,
  JournalStatus
} from "models/journal.model";
import type { JournalRow } from "pages/Journal/column.data";

export const PAGE_SIZE = 200; // 등록 한도와 같음

export const SLIP_TYPE_LABELS: Record<Journal["slipType"], string> = {
  "1": "출금",
  "2": "입금",
  "3": "대체"
};

export const STATUS_LABELS: Record<JournalStatus, string> = {
  unsent: "미전송",
  held: "보류",
  sent: "전송",
  failed: "전송 실패"
};

// 서버가 holdReason 값을 검증하지 않으므로 모르는 코드는 원문 그대로 보여 준다
const HOLD_REASON_LABELS: Record<string, string> = {
  bank_duplicate_suspect: "통장 분개 중복 의심",
  bank_ambiguous: "통장 원천 후보 2건 이상"
};

export const holdReasonLabel = (code?: string) =>
  code ? HOLD_REASON_LABELS[code] || code : "";

// 보류 해제 건너뜀 사유 (journal-api.md 3.4)
const RELEASE_SKIP_LABELS: Record<string, string> = {
  not_held: "보류 아님",
  already_sent: "이미 전송",
  confirmed: "마감",
  not_found: "전표 없음"
};

export const releaseSkipLabel = (reason?: string) =>
  reason ? RELEASE_SKIP_LABELS[reason] || reason : "";

// 전송 > 보류 > 전송 실패 > 미전송 순으로 판정 (journal-api.md 2장 상태)
export const journalStatus = (journal: Journal): JournalStatus => {
  if (journal.newzenSlipKeyNoList?.length) return "sent";
  if (journal.held) return "held";
  if (journal.newzenErrorRule) return "failed";
  return "unsent";
};

// 세무사랑 전송 대상이 될 수 있는 상태 (미전송·전송 실패)
export const isSendable = (journal: Journal) => {
  const status = journalStatus(journal);
  return status === "unsent" || status === "failed";
};

// 삭제할 수 있는 상태 (미전송·전송 실패·보류, 마감 아님)
export const isDeletable = (journal: Journal) =>
  !journal.isAccountingConfirmed && journalStatus(journal) !== "sent";

export const isBalanced = (journal: Journal) =>
  journal.totalDebit === journal.totalCredit;

export const CONFIRMED_SELECTION_MESSAGE =
  "선택한 데이터 중 마감처리된 데이터가 있어 변경할 수 없습니다.";
export const SENT_DELETE_MESSAGE =
  "세무사랑에 전송된 전표는 삭제할 수 없습니다.";

export type AccountNameMap = Map<string, string>;

export const toAccountNameMap = (
  accountCodes: JournalAccountCode[]
): AccountNameMap => new Map(accountCodes.map((ac) => [ac.code, ac.name]));

export const accountLabel = (code: string, nameMap: AccountNameMap) => {
  const name = nameMap.get(code);
  return name ? `${name}(${code})` : code;
};

// 출금(1)은 차변이 줄 계정·대변이 현금, 입금(2)은 그 반대, 대체(3)는 side 그대로
export const linesBySide = (journal: Journal, side: "D" | "C") => {
  if (journal.slipType === "3")
    return journal.lines.filter((line) => line.side === side);
  const lineSide = journal.slipType === "1" ? "D" : "C";
  return side === lineSide ? journal.lines : [];
};

const CASH_LABEL = "현금";

// 한쪽 계정이 여러 개면 첫 줄 계정 + "외 N"
export const sideAccountText = (
  journal: Journal,
  side: "D" | "C",
  nameMap: AccountNameMap
) => {
  const lines = linesBySide(journal, side);
  if (journal.slipType !== "3" && !lines.length) return CASH_LABEL;
  return firstWithRest(
    lines.map((line) => line.accountCode),
    (code) => nameMap.get(code) || code
  );
};

export const storeText = (journal: Journal) =>
  firstWithRest(
    journal.lines.map((line) => line.storeName || line.storeCode || ""),
    (name) => name
  );

export const memoText = (journal: Journal) =>
  journal.memo || journal.lines.find((line) => line.memo)?.memo || "";

const firstWithRest = (values: string[], format: (value: string) => string) => {
  const distinct = Array.from(new Set(values.filter(Boolean)));
  if (!distinct.length) return "";
  const first = format(distinct[0]);
  return distinct.length > 1 ? `${first} 외 ${distinct.length - 1}` : first;
};

// 거래처·적요 검색 (서버 파라미터가 없어 프런트에서 거른다)
export const matchesKeyword = (journal: Journal, keyword: string) => {
  if (!keyword) return true;
  const lowered = keyword.toLowerCase();
  const texts = [
    journal.memo,
    ...journal.lines.flatMap((line: JournalLine) => [line.storeName, line.memo])
  ];
  return texts.some((text) => text?.toLowerCase().includes(lowered));
};

export const sumBy = (journals: Journal[], key: "totalDebit" | "totalCredit") =>
  journals.reduce((acc, journal) => acc + (journal[key] || 0), 0);

export const refsText = (journal: Journal) => {
  if (!journal.refs?.length) return "";
  const count = (match: (ref: JournalRef) => boolean) =>
    journal.refs.filter((ref) => !ref.missing && match(ref)).length;
  const bank = count((ref) => ref.type === "bank");
  const buy = count((ref) => ref.type === "extra" && ref.extraType === "BUY");
  const sales = count(
    (ref) => ref.type === "extra" && ref.extraType === "SALES"
  );
  const extra = count((ref) => ref.type === "extra" && !ref.extraType);
  const missing = journal.refs.filter((ref) => ref.missing).length;
  return [
    bank ? `통장 ${bank}` : "",
    buy ? `기타매입 ${buy}` : "",
    sales ? `기타매출 ${sales}` : "",
    extra ? `기타 ${extra}` : "",
    missing ? `원천 없음 ${missing}` : ""
  ]
    .filter(Boolean)
    .join(" · ");
};

// 상태 열 정렬 순서: 처리할 것(보류·실패)이 먼저
const STATUS_ORDER: Record<JournalStatus, number> = {
  held: 0,
  failed: 1,
  unsent: 2,
  sent: 3
};

export const toJournalRow = (
  journal: Journal,
  nameMap: AccountNameMap
): JournalRow => ({
  ...journal,
  debitAccount: sideAccountText(journal, "D", nameMap),
  creditAccount: sideAccountText(journal, "C", nameMap),
  storeText: storeText(journal),
  memoText: memoText(journal),
  refsText: refsText(journal),
  statusOrder: STATUS_ORDER[journalStatus(journal)]
});

// yyyy-MM-dd / yyyyMMdd → yyyyMMdd (세무사랑 회사 기수·인수일 비교용)
export const toDigits = (date?: string) => (date || "").replace(/[^0-9]/g, "");
